import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Card,
    CardContent,
    CardHeader,
    Collapse,
    Stack,
    Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import type { PageEntityConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { useCommonTranslation } from 'c-translation';
import type {
    NotificationReportQuery,
    NotificationReportQueryDeltaCache,
    NotificationReportRun,
} from 'c-sdk';
import { PermissionName } from 'c-sdk';
import type { NotificationReport } from 'c-entity-types';
import { notificationsIcon } from 'c-main/icons';
import { useAPIClientRequest, useEntityPage } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import { Button, Checkbox, FlashMessage, SelectField, TextField } from 'c-components';
import { useEntityData, useUpdateEntityData } from 'c-data';
import { NetworkRequestState } from 'c-data-layer';
import type { DraftQuery, ExistingQueryDraft } from './notificationDetailLib';
import {
    buildColumnOptions,
    buildCreateQueryPayload,
    buildPauseToggle,
    buildReportUpdatePayload,
    buildUpdateQueryPayload,
    bytesLabelOf,
    canSaveDraftQueries,
    canSubmitExistingQuery,
    deltaKeyColumnsToText,
    detectedColumnsContainKeys,
    draftMatchesSavedQuery,
    formatReportDate,
    getQueryKey as queryKeyOf,
    getRequestErrorMessage,
    isExistingQuerySaveDisabled as isExistingQueryDraftSaveDisabled,
    isReportDetailsUnchanged,
    normalizeDeltaKeyColumns,
    normalizeQueryResponse as queryFromResponse,
    normalizeSource as sourceOf,
    queryRunsOf,
    queryToDraft,
} from './notificationDetailLib';

type NotificationReportWithQueries = NotificationReport & {
    queries?: NotificationReportQuery[] | null;
    last_run_at?: string | null;
    next_run_at?: string | null;
    created_by?: string | null;
};

type DeletedQueryToast = {
    reportId: number;
    query: ExistingQueryDraft;
};

type DetectedColumnsState = {
    columns: string[];
    isLoading: boolean;
    error?: string;
};

type DeltaCacheStatusState = {
    data?: NotificationReportQueryDeltaCache;
    isLoading: boolean;
    isClearing: boolean;
    error?: string;
    clearSuccess?: boolean;
};

type DeltaBaselineState = {
    isQueueing: boolean;
    isConfirming: boolean;
    message?: string;
    error?: string;
};

type ReportRunsState = {
    data: NotificationReportRun[];
    isLoading: boolean;
    error?: string;
};

const queryPanelSx = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    overflow: 'hidden',
    bgcolor: 'background.paper',
};

const queryHeaderSx = (isExpanded: boolean) => ({
    px: 2,
    py: 1.25,
    bgcolor: 'action.hover',
    borderBottom: isExpanded ? '1px solid' : 0,
    borderColor: 'divider',
});

const querySummaryPillSx = {
    px: 1,
    py: 0.25,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    bgcolor: 'background.default',
    color: 'text.secondary',
    lineHeight: 1.4,
};

const querySectionSx = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    p: 2,
};

const NotificationDetailPage = () => {
    const t = useCommonTranslation();
    const { id: reportId, entity } = useEntityPage<NotificationReport>();
    const { upsertEntity } = useEntityData<NotificationReport>('NotificationReport');
    const { update, getUpdatingById, resetUpdatingById } =
        useUpdateEntityData<NotificationReport>('NotificationReport');
    const [querySources, setQuerySources] = useState<Record<string, string>>({});
    const [existingQueryEdits, setExistingQueryEdits] = useState<
        Record<string, ExistingQueryDraft>
    >({});
    const [deletingQueries, setDeletingQueries] = useState<Record<string, boolean>>({});
    const [deletedToast, setDeletedToast] = useState<DeletedQueryToast | null>(null);
    const [draftQueries, setDraftQueries] = useState<DraftQuery[]>([]);
    const [hiddenQueries, setHiddenQueries] = useState<Record<string, boolean>>({});
    const [nameDraft, setNameDraft] = useState<string>('');
    const [emailsDraft, setEmailsDraft] = useState<string>('');
    const [subjectDraft, setSubjectDraft] = useState<string>('');
    const [bodyDraft, setBodyDraft] = useState<string>('');
    const [intervalDraft, setIntervalDraft] = useState<string>('');
    const [localQueries, setLocalQueries] = useState<NotificationReportQuery[]>([]);
    const [expandedQueries, setExpandedQueries] = useState<Record<string, boolean>>({});
    const [detectedColumns, setDetectedColumns] = useState<Record<string, DetectedColumnsState>>(
        {},
    );
    const [deltaCacheStatuses, setDeltaCacheStatuses] = useState<
        Record<string, DeltaCacheStatusState>
    >({});
    const [confirmingClearDeltaCache, setConfirmingClearDeltaCache] = useState<
        Record<string, boolean>
    >({});
    const [deltaBaselineStates, setDeltaBaselineStates] = useState<
        Record<string, DeltaBaselineState>
    >({});
    const [reportRuns, setReportRuns] = useState<ReportRunsState>({
        data: [],
        isLoading: false,
    });
    const [expandedRunKeys, setExpandedRunKeys] = useState<Record<string, boolean>>({});
    const deleteTimers = useRef<Record<string, number>>({});
    const lastSavedDraftIdsRef = useRef<Set<string>>(new Set());
    const toastTimer = useRef<number | null>(null);
    const {
        start: createReportQuery,
        isLoading: isSavingQueries,
        error: saveError,
        reset: resetSave,
    } = useAPIClientRequest(ApiClient.Notifications.createReportQuery);
    const {
        start: updateReportQuery,
        isLoading: isUpdatingQuery,
        error: updateQueryError,
        reset: resetUpdateQuery,
    } = useAPIClientRequest(ApiClient.Notifications.updateReportQuery);
    const {
        start: deleteReportQuery,
        isLoading: isDeletingQuery,
        error: deleteQueryError,
        reset: resetDeleteQuery,
    } = useAPIClientRequest(ApiClient.Notifications.deleteReportQuery);
    const {
        start: previewReport,
        isLoading: isPreviewLoading,
        error: previewError,
        data: previewData,
        reset: resetPreview,
    } = useAPIClientRequest(ApiClient.Notifications.previewReport);
    const { start: detectReportQueryColumns } = useAPIClientRequest(
        ApiClient.Notifications.detectReportQueryColumns,
    );
    const { start: getReportQueryDeltaCache } = useAPIClientRequest(
        ApiClient.Notifications.getReportQueryDeltaCache,
    );
    const { start: clearReportQueryDeltaCache } = useAPIClientRequest(
        ApiClient.Notifications.clearReportQueryDeltaCache,
    );
    const { start: seedReportQueryDeltaBaseline } = useAPIClientRequest(
        ApiClient.Notifications.seedReportQueryDeltaBaseline,
    );
    const { start: getReportRuns } = useAPIClientRequest(ApiClient.Notifications.getReportRuns);

    const report = entity as NotificationReportWithQueries | undefined;
    const queries = localQueries;
    const emailAddresses = useMemo(() => report?.email_addresses ?? [], [report?.email_addresses]);
    const updateState = useMemo(
        () => (Number.isFinite(reportId) ? getUpdatingById(reportId) : null),
        [getUpdatingById, reportId],
    );
    const isUpdateNotificationDisabled = useMemo(() => {
        if (updateState?.state === NetworkRequestState.InProgress) return true;
        return isReportDetailsUnchanged(report, {
            name: nameDraft,
            subject: subjectDraft,
            body: bodyDraft,
            interval: intervalDraft,
            emails: emailsDraft,
        });
    }, [
        bodyDraft,
        emailsDraft,
        intervalDraft,
        nameDraft,
        report,
        subjectDraft,
        updateState?.state,
    ]);

    const getQueryKey = useCallback(queryKeyOf, []);

    const normalizeQueryResponse = useCallback(queryFromResponse, []);

    const visibleQueries = useMemo(
        () =>
            queries
                .map((query, index) => ({ query, index }))
                .filter(({ query, index }) => !hiddenQueries[getQueryKey(query, index)]),
        [getQueryKey, hiddenQueries, queries],
    );
    const existingQueryKeys = useMemo(
        () => visibleQueries.map(({ query, index }) => getQueryKey(query, index)),
        [getQueryKey, visibleQueries],
    );
    const areAllQueriesExpanded = useMemo(
        () => existingQueryKeys.length > 0 && existingQueryKeys.every(key => expandedQueries[key]),
        [existingQueryKeys, expandedQueries],
    );

    const sourceOptions = useMemo(
        () => [
            { label: 'athena', value: 'athena' },
            { label: 'mysql', value: 'mysql' },
        ],
        [],
    );
    const normalizeSource = useCallback(sourceOf, []);

    const formatDate = useCallback(
        (value?: string | null) =>
            formatReportDate(value) ?? t('Modules.Admin.NotificationDetail.emptyValue'),
        [t],
    );

    const formatBytes = useCallback(
        (value?: number | null) => {
            const label = bytesLabelOf(value);
            switch (label.key) {
                case 'bytesLabel':
                    return t('Modules.Admin.NotificationDetail.bytesLabel', { count: label.count });
                case 'kilobytesLabel':
                    return t('Modules.Admin.NotificationDetail.kilobytesLabel', {
                        count: label.count,
                    });
                case 'megabytesLabel':
                    return t('Modules.Admin.NotificationDetail.megabytesLabel', {
                        count: label.count,
                    });
                default:
                    return t('Modules.Admin.NotificationDetail.emptyValue');
            }
        },
        [t],
    );

    useEffect(() => {
        setDraftQueries([]);
        setHiddenQueries({});
        setQuerySources({});
        setExistingQueryEdits({});
        setDeletingQueries({});
        setDeletedToast(null);
        setLocalQueries([]);
        setExpandedQueries({});
        setDetectedColumns({});
        setDeltaCacheStatuses({});
        setConfirmingClearDeltaCache({});
        setDeltaBaselineStates({});
        setReportRuns({ data: [], isLoading: false });
        setExpandedRunKeys({});
        resetPreview();
    }, [reportId, resetPreview]);

    useEffect(() => {
        setLocalQueries(report?.queries ?? []);
    }, [report?.queries]);
    useEffect(
        () => () => {
            Object.values(deleteTimers.current).forEach(timerId => window.clearTimeout(timerId));
            deleteTimers.current = {};
            if (toastTimer.current) {
                window.clearTimeout(toastTimer.current);
                toastTimer.current = null;
            }
        },
        [],
    );

    useEffect(() => {
        setEmailsDraft(emailAddresses.join('\n'));
    }, [emailAddresses]);

    useEffect(() => {
        setNameDraft(report?.name ?? '');
        setSubjectDraft(report?.email_subject ?? '');
        setBodyDraft(report?.email_body ?? '');
    }, [report?.email_body, report?.email_subject, report?.name]);

    useEffect(() => {
        setIntervalDraft(report?.interval ?? '');
    }, [report?.interval]);

    useEffect(() => {
        if (!Number.isFinite(reportId)) return;
        if (updateState?.state === NetworkRequestState.Success) {
            resetUpdatingById(reportId);
        }
    }, [reportId, resetUpdatingById, updateState?.state]);

    useEffect(() => {
        if (queries.length === 0) return;
        setQuerySources(prev => {
            const next = { ...prev };
            queries.forEach((query, index) => {
                const key = getQueryKey(query, index);
                if (!next[key]) {
                    next[key] = query.source ?? '';
                }
            });
            return next;
        });
        setExistingQueryEdits(prev => {
            const next = { ...prev };
            queries.forEach((query, index) => {
                const key = getQueryKey(query, index);
                if (!next[key]) {
                    next[key] = queryToDraft(query);
                }
            });
            return next;
        });
    }, [getQueryKey, queries]);

    useEffect(() => {
        if (draftQueries.length === 0 || queries.length === 0) return;
        setDraftQueries(prev => {
            const lastSavedDraftIds = lastSavedDraftIdsRef.current;
            if (lastSavedDraftIds.size === 0) return prev;
            const next = prev.filter(
                draft =>
                    !(
                        lastSavedDraftIds.has(draft.id) &&
                        queries.some(existing => draftMatchesSavedQuery(existing, draft))
                    ),
            );
            if (next.length !== prev.length) {
                lastSavedDraftIdsRef.current = new Set();
            }
            return next.length === prev.length ? prev : next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queries]);

    const getExistingQueryDraft = useCallback(
        (query: NotificationReportQuery, index: number) => {
            const queryKey = getQueryKey(query, index);
            return existingQueryEdits[queryKey] ?? queryToDraft(query);
        },
        [existingQueryEdits, getQueryKey],
    );

    const appendQueriesToState = useCallback(
        (nextQueries: NotificationReportQuery[]) => {
            if (nextQueries.length === 0) return;
            setLocalQueries(prev => [...prev, ...nextQueries]);
            if (!report) return;
            upsertEntity({
                ...(report as NotificationReport),
                queries: [...(report.queries ?? []), ...nextQueries],
            } as NotificationReport);
        },
        [report, upsertEntity],
    );

    const hydrateQueryMeta = useCallback((queriesToAdd: NotificationReportQuery[]) => {
        if (queriesToAdd.length === 0) return;
        setExistingQueryEdits(prev => {
            const next = { ...prev };
            queriesToAdd.forEach(query => {
                const key = String(query?.id ?? query?.filename);
                if (!next[key]) {
                    next[key] = queryToDraft(query);
                }
            });
            return next;
        });
        setQuerySources(prev => {
            const next = { ...prev };
            queriesToAdd.forEach(query => {
                const key = String(query?.id ?? query?.filename);
                if (!next[key]) {
                    next[key] = query?.source ?? '';
                }
            });
            return next;
        });
    }, []);

    const isExistingQuerySaveDisabled = useCallback(
        (query: NotificationReportQuery, index: number) => {
            if (isUpdatingQuery) return true;
            const draft = getExistingQueryDraft(query, index);
            return isExistingQueryDraftSaveDisabled(draft, query);
        },
        [getExistingQueryDraft, isUpdatingQuery],
    );

    const clearDetectedColumns = useCallback((queryKey: string) => {
        setDetectedColumns(prev => {
            if (!prev[queryKey]) return prev;

            const next = { ...prev };
            delete next[queryKey];

            return next;
        });
    }, []);

    const handleSourceChange = useCallback(
        (queryKey: string, queryDraft: ExistingQueryDraft) => (event: any) => {
            const nextValue = String(event.target.value ?? '');
            clearDetectedColumns(queryKey);
            setQuerySources(prev => ({ ...prev, [queryKey]: nextValue }));
            setExistingQueryEdits(prev => ({
                ...prev,
                [queryKey]: {
                    ...(prev[queryKey] ?? queryDraft),
                    source: nextValue,
                },
            }));
        },
        [clearDetectedColumns],
    );

    const toggleQueryExpanded = useCallback((queryKey: string) => {
        setExpandedQueries(prev => ({ ...prev, [queryKey]: !prev[queryKey] }));
    }, []);
    const toggleRunExpanded = useCallback((runKey: string) => {
        setExpandedRunKeys(prev => ({ ...prev, [runKey]: !prev[runKey] }));
    }, []);
    const toggleAllQueriesExpanded = useCallback(() => {
        setExpandedQueries(prev => {
            const next: Record<string, boolean> = { ...prev };
            const shouldExpand = !areAllQueriesExpanded;
            existingQueryKeys.forEach(key => {
                next[key] = shouldExpand;
            });
            return next;
        });
    }, [areAllQueriesExpanded, existingQueryKeys]);

    const handleExistingQueryChange = useCallback(
        (queryKey: string, queryDraft: ExistingQueryDraft) => (event: any) => {
            const nextValue = event.target.value ?? '';
            clearDetectedColumns(queryKey);
            setExistingQueryEdits(prev => ({
                ...prev,
                [queryKey]: {
                    ...(prev[queryKey] ?? queryDraft),
                    query: nextValue,
                },
            }));
        },
        [clearDetectedColumns],
    );

    const handleExistingFilenameChange = useCallback(
        (queryKey: string, queryDraft: ExistingQueryDraft) => (event: any) => {
            const nextValue = event.target.value ?? '';
            setExistingQueryEdits(prev => ({
                ...prev,
                [queryKey]: {
                    ...(prev[queryKey] ?? queryDraft),
                    filename: nextValue,
                },
            }));
        },
        [],
    );

    const handleExistingSendNewRowsChange = useCallback(
        (queryKey: string, queryDraft: ExistingQueryDraft) => (value: boolean) => {
            setExistingQueryEdits(prev => ({
                ...prev,
                [queryKey]: {
                    ...(prev[queryKey] ?? queryDraft),
                    send_new_rows: value,
                },
            }));
        },
        [],
    );

    const handleExistingSendUpdatedRowsChange = useCallback(
        (queryKey: string, queryDraft: ExistingQueryDraft) => (value: boolean) => {
            setExistingQueryEdits(prev => ({
                ...prev,
                [queryKey]: {
                    ...(prev[queryKey] ?? queryDraft),
                    send_updated_rows: value,
                },
            }));
        },
        [],
    );

    const handleExistingDeltaKeyColumnsChange = useCallback(
        (queryKey: string, queryDraft: ExistingQueryDraft) => (event: any) => {
            const nextValue = event.target.value ?? '';
            setExistingQueryEdits(prev => ({
                ...prev,
                [queryKey]: {
                    ...(prev[queryKey] ?? queryDraft),
                    delta_key_columns: nextValue,
                },
            }));
        },
        [],
    );

    const handleAddQuery = useCallback(() => {
        setDraftQueries(prev => {
            const id = `draft-${Date.now()}-${prev.length}`;
            setExpandedQueries(expanded => ({ ...expanded, [id]: true }));
            return [
                ...prev,
                {
                    id,
                    source: '',
                    query: '',
                    filename: '',
                    send_new_rows: false,
                    send_updated_rows: false,
                    delta_key_columns: '',
                },
            ];
        });
    }, []);

    const handleDraftSourceChange = useCallback(
        (queryId: string) => (event: any) => {
            const nextValue = String(event.target.value ?? '');
            clearDetectedColumns(queryId);
            setDraftQueries(prev =>
                prev.map(query => (query.id === queryId ? { ...query, source: nextValue } : query)),
            );
        },
        [clearDetectedColumns],
    );

    const handleDraftQueryChange = useCallback(
        (queryId: string) => (event: any) => {
            const nextValue = event.target.value;
            clearDetectedColumns(queryId);
            setDraftQueries(prev =>
                prev.map(query => (query.id === queryId ? { ...query, query: nextValue } : query)),
            );
        },
        [clearDetectedColumns],
    );

    const handleDraftFilenameChange = useCallback(
        (queryId: string) => (event: any) => {
            const nextValue = event.target.value;
            setDraftQueries(prev =>
                prev.map(query =>
                    query.id === queryId ? { ...query, filename: nextValue } : query,
                ),
            );
        },
        [],
    );

    const handleDraftSendNewRowsChange = useCallback(
        (queryId: string) => (value: boolean) => {
            setDraftQueries(prev =>
                prev.map(query =>
                    query.id === queryId ? { ...query, send_new_rows: value } : query,
                ),
            );
        },
        [],
    );

    const handleDraftSendUpdatedRowsChange = useCallback(
        (queryId: string) => (value: boolean) => {
            setDraftQueries(prev =>
                prev.map(query =>
                    query.id === queryId ? { ...query, send_updated_rows: value } : query,
                ),
            );
        },
        [],
    );

    const handleDraftDeltaKeyColumnsChange = useCallback(
        (queryId: string) => (event: any) => {
            const nextValue = event.target.value;
            setDraftQueries(prev =>
                prev.map(query =>
                    query.id === queryId ? { ...query, delta_key_columns: nextValue } : query,
                ),
            );
        },
        [],
    );

    const normalizeDetectedColumnsResponse = useCallback((response: any): string[] => {
        const payload = response?.data?.data ?? response?.data ?? response;
        const columns = payload?.columns;

        return Array.isArray(columns)
            ? columns.map(column => String(column).trim()).filter(column => column.length > 0)
            : [];
    }, []);

    const normalizeDeltaCacheResponse = useCallback(
        (response: any): NotificationReportQueryDeltaCache | null => {
            const payload = response?.data?.data ?? response?.data ?? response;
            return payload?.delta_cache ?? null;
        },
        [],
    );

    const normalizeReportRunsResponse = useCallback((response: any): NotificationReportRun[] => {
        const payload = response?.data?.data ?? response?.data ?? response;
        const runs = payload?.data ?? payload;

        return Array.isArray(runs) ? (runs as NotificationReportRun[]) : [];
    }, []);

    const refreshReportRuns = useCallback(async () => {
        if (!Number.isFinite(reportId)) return;

        setReportRuns(prev => ({ ...prev, isLoading: true, error: undefined }));

        try {
            const response = await getReportRuns(reportId);
            setReportRuns({
                data: normalizeReportRunsResponse(response),
                isLoading: false,
            });
        } catch (error) {
            setReportRuns(prev => ({
                ...prev,
                isLoading: false,
                error: getRequestErrorMessage(
                    error,
                    t('Modules.Admin.NotificationDetail.reportRunsLoadErrorFallback'),
                ),
            }));
        }
    }, [getReportRuns, normalizeReportRunsResponse, reportId, t]);

    const getDeltaCacheStatusLabel = useCallback(
        (state?: DeltaCacheStatusState): string => {
            if (state?.isLoading) {
                return t('Modules.Admin.NotificationDetail.deltaCacheLoadingLabel');
            }

            if (state?.error) return state.error;

            if (state?.clearSuccess) {
                return t('Modules.Admin.NotificationDetail.deltaCacheClearedLabel');
            }

            const cache = state?.data;
            if (!cache) return t('Modules.Admin.NotificationDetail.deltaCacheLoadingLabel');

            if (!cache.is_readable) {
                return (
                    cache.message ?? t('Modules.Admin.NotificationDetail.deltaCacheUnreadableLabel')
                );
            }

            if (cache.is_set) {
                return t('Modules.Admin.NotificationDetail.deltaCacheRowsCachedLabel', {
                    count: cache.row_count ?? 0,
                });
            }

            if (cache.has_any_cache) {
                return t('Modules.Admin.NotificationDetail.deltaCacheInactiveButStoredLabel');
            }

            return t('Modules.Admin.NotificationDetail.deltaCacheEmptyLabel');
        },
        [t],
    );

    const refreshDeltaCacheStatus = useCallback(
        async (query: NotificationReportQuery, index: number) => {
            if (!query?.id) return;

            const queryKey = getQueryKey(query, index);
            setDeltaCacheStatuses(prev => ({
                ...prev,
                [queryKey]: {
                    data: prev[queryKey]?.data,
                    isLoading: true,
                    isClearing: prev[queryKey]?.isClearing ?? false,
                    clearSuccess: false,
                },
            }));

            try {
                const response = await getReportQueryDeltaCache(query.id);
                const data = normalizeDeltaCacheResponse(response);

                setDeltaCacheStatuses(prev => ({
                    ...prev,
                    [queryKey]: {
                        data: data ?? undefined,
                        isLoading: false,
                        isClearing: false,
                    },
                }));
            } catch (error) {
                setDeltaCacheStatuses(prev => ({
                    ...prev,
                    [queryKey]: {
                        data: prev[queryKey]?.data,
                        isLoading: false,
                        isClearing: false,
                        error: getRequestErrorMessage(
                            error,
                            t('Modules.Admin.NotificationDetail.deltaCacheLoadErrorFallback'),
                        ),
                    },
                }));
            }
        },
        [getQueryKey, getReportQueryDeltaCache, normalizeDeltaCacheResponse, t],
    );

    useEffect(() => {
        if (queries.length === 0) {
            setDeltaCacheStatuses({});
            return;
        }

        const nextKeys = new Set<string>();
        const statusQueries = queries
            .map((query, index) => ({ query, index }))
            .filter(({ query }) => Boolean(query?.id));

        statusQueries.forEach(({ query, index }) => {
            nextKeys.add(getQueryKey(query, index));
        });

        setDeltaCacheStatuses(prev => {
            const next = { ...prev };
            Object.keys(next).forEach(key => {
                if (!nextKeys.has(key)) delete next[key];
            });
            return next;
        });

        const fetchStatuses = statusQueries.reduce<Promise<void>>(
            (promise, { query, index }) =>
                promise.then(() => refreshDeltaCacheStatus(query, index)),
            Promise.resolve(),
        );
        fetchStatuses.catch(() => undefined);
    }, [getQueryKey, queries, refreshDeltaCacheStatus]);

    useEffect(() => {
        refreshReportRuns().catch(() => undefined);
    }, [refreshReportRuns]);

    const handleDetectColumns = useCallback(
        (queryKey: string, queryDraft: ExistingQueryDraft | DraftQuery) => async () => {
            const source =
                queryDraft.source === 'mysql' || queryDraft.source === 'athena'
                    ? queryDraft.source
                    : null;
            const query = queryDraft.query.trim();

            if (!source || !query) return;

            setDetectedColumns(prev => ({
                ...prev,
                [queryKey]: {
                    columns: prev[queryKey]?.columns ?? [],
                    isLoading: true,
                    error: undefined,
                },
            }));

            try {
                const response = await detectReportQueryColumns({ source, query });
                const columns = normalizeDetectedColumnsResponse(response);

                setDetectedColumns(prev => ({
                    ...prev,
                    [queryKey]: {
                        columns,
                        isLoading: false,
                    },
                }));
            } catch (error) {
                setDetectedColumns(prev => ({
                    ...prev,
                    [queryKey]: {
                        columns: prev[queryKey]?.columns ?? [],
                        isLoading: false,
                        error: getRequestErrorMessage(
                            error,
                            t('Modules.Admin.NotificationDetail.detectColumnsErrorFallback'),
                        ),
                    },
                }));
            }
        },
        [detectReportQueryColumns, normalizeDetectedColumnsResponse, t],
    );

    const handleCloneExistingQuery = useCallback(
        (query: NotificationReportQuery, index: number) => {
            const queryKey = getQueryKey(query, index);
            const sourceValue =
                existingQueryEdits[queryKey]?.source ??
                querySources[queryKey] ??
                query.source ??
                '';
            const queryValue = existingQueryEdits[queryKey]?.query ?? query.query ?? '';
            const filenameValue = existingQueryEdits[queryKey]?.filename ?? query.filename ?? '';
            const sendNewRowsValue =
                existingQueryEdits[queryKey]?.send_new_rows ?? Boolean(query.send_new_rows);
            const sendUpdatedRowsValue =
                existingQueryEdits[queryKey]?.send_updated_rows ?? Boolean(query.send_updated_rows);
            const deltaKeyColumnsValue =
                existingQueryEdits[queryKey]?.delta_key_columns ??
                deltaKeyColumnsToText(query.delta_key_columns);
            setDraftQueries(prev => {
                const id = `draft-${Date.now()}-${prev.length}`;
                setExpandedQueries(expanded => ({ ...expanded, [id]: true }));
                return [
                    ...prev,
                    {
                        id,
                        source: sourceValue,
                        query: queryValue,
                        filename: filenameValue,
                        send_new_rows: sendNewRowsValue,
                        send_updated_rows: sendUpdatedRowsValue,
                        delta_key_columns: deltaKeyColumnsValue,
                    },
                ];
            });
        },
        [existingQueryEdits, getQueryKey, querySources],
    );

    const handleDeleteExistingQuery = useCallback(
        async (query: NotificationReportQuery, index: number) => {
            const queryKey = getQueryKey(query, index);
            if (!query?.id) return;
            resetDeleteQuery();
            try {
                await deleteReportQuery(query.id);
                setDeletingQueries(prev => ({ ...prev, [queryKey]: true }));
                if (deleteTimers.current[queryKey]) {
                    window.clearTimeout(deleteTimers.current[queryKey]);
                }
                deleteTimers.current[queryKey] = window.setTimeout(() => {
                    setHiddenQueries(prev => ({ ...prev, [queryKey]: true }));
                    setExistingQueryEdits(prev => {
                        const next = { ...prev };
                        delete next[queryKey];
                        return next;
                    });
                    setQuerySources(prev => {
                        const next = { ...prev };
                        delete next[queryKey];
                        return next;
                    });
                    setExpandedQueries(prev => {
                        const next = { ...prev };
                        delete next[queryKey];
                        return next;
                    });
                    setDeltaCacheStatuses(prev => {
                        const next = { ...prev };
                        delete next[queryKey];
                        return next;
                    });
                    setConfirmingClearDeltaCache(prev => {
                        const next = { ...prev };
                        delete next[queryKey];
                        return next;
                    });
                    setDeltaBaselineStates(prev => {
                        const next = { ...prev };
                        delete next[queryKey];
                        return next;
                    });
                    setDeletingQueries(prev => {
                        const next = { ...prev };
                        delete next[queryKey];
                        return next;
                    });
                    delete deleteTimers.current[queryKey];
                }, 200);
                setLocalQueries(prev => prev.filter(item => item?.id !== query.id));

                const draft = existingQueryEdits[queryKey] ?? queryToDraft(query);
                setDeletedToast({ reportId, query: draft });
                if (toastTimer.current) {
                    window.clearTimeout(toastTimer.current);
                }
                toastTimer.current = window.setTimeout(() => {
                    setDeletedToast(null);
                    toastTimer.current = null;
                }, 5000);
            } catch {
                // Errors are surfaced via deleteQueryError.
            }
        },
        [deleteReportQuery, existingQueryEdits, getQueryKey, reportId, resetDeleteQuery],
    );

    const handleClearDeltaCache = useCallback(
        (query: NotificationReportQuery, index: number) => async () => {
            if (!query?.id) return;

            const queryKey = getQueryKey(query, index);
            if (!confirmingClearDeltaCache[queryKey]) {
                setConfirmingClearDeltaCache(prev => ({ ...prev, [queryKey]: true }));
                return;
            }

            setDeltaCacheStatuses(prev => ({
                ...prev,
                [queryKey]: {
                    data: prev[queryKey]?.data,
                    isLoading: false,
                    isClearing: true,
                    clearSuccess: false,
                },
            }));

            try {
                const response = await clearReportQueryDeltaCache(query.id);
                const data = normalizeDeltaCacheResponse(response);
                setConfirmingClearDeltaCache(prev => ({ ...prev, [queryKey]: false }));

                setDeltaCacheStatuses(prev => ({
                    ...prev,
                    [queryKey]: {
                        data: data ?? undefined,
                        isLoading: false,
                        isClearing: false,
                        clearSuccess: true,
                    },
                }));
            } catch (error) {
                setConfirmingClearDeltaCache(prev => ({ ...prev, [queryKey]: false }));
                setDeltaCacheStatuses(prev => ({
                    ...prev,
                    [queryKey]: {
                        data: prev[queryKey]?.data,
                        isLoading: false,
                        isClearing: false,
                        error: getRequestErrorMessage(
                            error,
                            t('Modules.Admin.NotificationDetail.deltaCacheClearErrorFallback'),
                        ),
                    },
                }));
            }
        },
        [
            clearReportQueryDeltaCache,
            confirmingClearDeltaCache,
            getQueryKey,
            normalizeDeltaCacheResponse,
            t,
        ],
    );

    const handleSeedDeltaBaseline = useCallback(
        (query: NotificationReportQuery, index: number) => async () => {
            if (!query?.id) return;

            const queryKey = getQueryKey(query, index);
            const baselineState = deltaBaselineStates[queryKey];

            if (!baselineState?.isConfirming) {
                setDeltaBaselineStates(prev => ({
                    ...prev,
                    [queryKey]: {
                        isQueueing: false,
                        isConfirming: true,
                    },
                }));
                return;
            }

            setDeltaBaselineStates(prev => ({
                ...prev,
                [queryKey]: {
                    isQueueing: true,
                    isConfirming: false,
                },
            }));

            try {
                const response = await seedReportQueryDeltaBaseline(query.id);
                const rawResponse = response as any;
                const payload = rawResponse?.data?.data ?? rawResponse?.data ?? rawResponse;
                const message = payload?.job_status_id
                    ? `${payload.message} ${t('Modules.Admin.NotificationDetail.jobStatusLabel', {
                          id: payload.job_status_id,
                      })}`
                    : payload?.message;

                setDeltaBaselineStates(prev => ({
                    ...prev,
                    [queryKey]: {
                        isQueueing: false,
                        isConfirming: false,
                        message:
                            message ??
                            t('Modules.Admin.NotificationDetail.deltaBaselineQueuedLabel'),
                    },
                }));
                refreshDeltaCacheStatus(query, index).catch(() => undefined);
                refreshReportRuns().catch(() => undefined);
            } catch (error) {
                setDeltaBaselineStates(prev => ({
                    ...prev,
                    [queryKey]: {
                        isQueueing: false,
                        isConfirming: false,
                        error: getRequestErrorMessage(
                            error,
                            t('Modules.Admin.NotificationDetail.deltaBaselineQueueErrorFallback'),
                        ),
                    },
                }));
            }
        },
        [
            deltaBaselineStates,
            getQueryKey,
            refreshDeltaCacheStatus,
            refreshReportRuns,
            seedReportQueryDeltaBaseline,
            t,
        ],
    );

    const handleCloneDraftQuery = useCallback((query: DraftQuery) => {
        setDraftQueries(prev => {
            const id = `draft-${Date.now()}-${prev.length}`;
            setExpandedQueries(expanded => ({ ...expanded, [id]: true }));
            return [
                ...prev,
                {
                    id,
                    source: query.source,
                    query: query.query,
                    filename: query.filename,
                    send_new_rows: query.send_new_rows,
                    send_updated_rows: query.send_updated_rows,
                    delta_key_columns: query.delta_key_columns,
                },
            ];
        });
    }, []);

    const handleDeleteDraftQuery = useCallback((queryId: string) => {
        setDraftQueries(prev => prev.filter(query => query.id !== queryId));
    }, []);

    const canSaveQueries = useMemo(() => canSaveDraftQueries(draftQueries), [draftQueries]);

    const handleSaveQueries = useCallback(async () => {
        if (!report) return;
        if (!canSaveQueries) return;
        if (isSavingQueries) return;

        resetSave();

        try {
            lastSavedDraftIdsRef.current = new Set(draftQueries.map(query => query.id));
            const responses = await Promise.all(
                draftQueries.map(query =>
                    createReportQuery(buildCreateQueryPayload(report.id, query)),
                ),
            );
            const createdQueries = responses
                .map(response => normalizeQueryResponse(response))
                .filter(Boolean) as NotificationReportQuery[];
            if (createdQueries.length > 0) {
                hydrateQueryMeta(createdQueries);
                appendQueriesToState(createdQueries);
            }
            setDraftQueries([]);
        } catch {
            // Errors are surfaced via saveError.
        }
    }, [
        canSaveQueries,
        createReportQuery,
        draftQueries,
        isSavingQueries,
        report,
        resetSave,
        appendQueriesToState,
        hydrateQueryMeta,
        normalizeQueryResponse,
    ]);

    const handleNotificationUpdate = useCallback(() => {
        if (!report) return;
        const payload = buildReportUpdatePayload(report as NotificationReport, {
            name: nameDraft,
            subject: subjectDraft,
            body: bodyDraft,
            interval: intervalDraft,
            emails: emailsDraft,
        });
        update(report.id, payload as NotificationReport);
        upsertEntity({
            ...(report as NotificationReport),
            ...payload,
        } as NotificationReport);
    }, [
        bodyDraft,
        emailsDraft,
        intervalDraft,
        nameDraft,
        report,
        subjectDraft,
        update,
        upsertEntity,
    ]);

    const handlePauseToggle = useCallback(() => {
        if (!report) return;
        const { request, optimistic } = buildPauseToggle(report as NotificationReport, new Date());

        update(report.id, request as NotificationReport);
        upsertEntity(optimistic as NotificationReport);
    }, [report, update, upsertEntity]);

    const handleSaveExistingQuery = useCallback(
        async (query: NotificationReportQuery, index: number) => {
            if (!query?.id) return;
            const queryKey = getQueryKey(query, index);
            const draft = getExistingQueryDraft(query, index);
            if (!canSubmitExistingQuery(draft)) return;
            const payload = buildUpdateQueryPayload(draft);
            const deltaKeyColumns = payload.delta_key_columns;
            resetUpdateQuery();
            try {
                await updateReportQuery(query.id, payload);
                if (!report) return;
                setLocalQueries(prev =>
                    prev.map(item =>
                        item?.id === query.id
                            ? {
                                  ...item,
                                  source: normalizeSource(draft.source),
                                  query: draft.query,
                                  filename: draft.filename,
                                  send_new_rows: draft.send_new_rows,
                                  send_updated_rows: draft.send_updated_rows,
                                  delta_key_columns: deltaKeyColumns,
                              }
                            : item,
                    ),
                );
                const nextQueries = (report.queries ?? []).map(existing =>
                    existing?.id === query.id
                        ? {
                              ...existing,
                              source: normalizeSource(draft.source),
                              query: draft.query,
                              filename: draft.filename,
                              send_new_rows: draft.send_new_rows,
                              send_updated_rows: draft.send_updated_rows,
                              delta_key_columns: deltaKeyColumns,
                          }
                        : existing,
                );
                upsertEntity({
                    ...(report as NotificationReport),
                    queries: nextQueries,
                } as NotificationReport);
                setExistingQueryEdits(prev => ({
                    ...prev,
                    [queryKey]: {
                        source: normalizeSource(draft.source),
                        query: draft.query,
                        filename: draft.filename,
                        send_new_rows: draft.send_new_rows,
                        send_updated_rows: draft.send_updated_rows,
                        delta_key_columns: deltaKeyColumns.join('\n'),
                    },
                }));
                refreshDeltaCacheStatus(
                    {
                        ...query,
                        source: normalizeSource(draft.source),
                        query: draft.query,
                        filename: draft.filename,
                        send_new_rows: draft.send_new_rows,
                        send_updated_rows: draft.send_updated_rows,
                        delta_key_columns: deltaKeyColumns,
                    },
                    index,
                ).catch(() => undefined);
            } catch {
                // Errors are surfaced via updateQueryError.
            }
        },
        [
            getExistingQueryDraft,
            getQueryKey,
            normalizeSource,
            refreshDeltaCacheStatus,
            resetUpdateQuery,
            setExistingQueryEdits,
            updateReportQuery,
            report,
            upsertEntity,
        ],
    );

    const handleUndoDelete = useCallback(async () => {
        if (!deletedToast) return;
        resetSave();
        try {
            const response = await createReportQuery(
                buildCreateQueryPayload(deletedToast.reportId, deletedToast.query),
            );
            const createdData = normalizeQueryResponse(response);
            if (!createdData) return;
            const created = {
                ...deletedToast.query,
                ...createdData,
            } as NotificationReportQuery;
            hydrateQueryMeta([created]);
            appendQueriesToState([created]);
        } catch {
            // Errors are surfaced via saveError.
        } finally {
            setDeletedToast(null);
            if (toastTimer.current) {
                window.clearTimeout(toastTimer.current);
                toastTimer.current = null;
            }
        }
    }, [
        appendQueriesToState,
        createReportQuery,
        deletedToast,
        hydrateQueryMeta,
        normalizeQueryResponse,
        resetSave,
    ]);
    const previewSuccessMessage = useMemo(() => {
        const payload = previewData?.data as { success?: boolean; message?: string } | undefined;
        if (payload?.message) return payload.message;
        if (payload?.success)
            return t('Modules.Admin.NotificationDetail.previewQueuedFallbackLabel');
        return '';
    }, [previewData, t]);
    const getPreviewHandler = useCallback(async () => {
        if (!Number.isFinite(reportId)) return;
        resetPreview();
        try {
            await previewReport(reportId);
            refreshReportRuns().catch(() => undefined);
        } catch {
            // Errors are surfaced via previewError.
        }
    }, [previewReport, refreshReportRuns, reportId, resetPreview]);

    return (
        <Stack spacing={2}>
            <Card>
                <CardHeader title={t('Modules.Admin.NotificationDetail.metaTitle')} />
                <CardContent>
                    {!Number.isFinite(reportId) && (
                        <Typography color="error">
                            {t('Modules.Admin.NotificationDetail.invalidId')}
                        </Typography>
                    )}
                    {Number.isFinite(reportId) && report && (
                        <Stack spacing={1.5}>
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={2}
                                alignItems={{ xs: 'stretch', md: 'center' }}
                            >
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: '1fr',
                                            sm: 'repeat(2, minmax(0, 1fr))',
                                            lg: 'repeat(5, minmax(0, 1fr))',
                                        },
                                        gap: 2,
                                        flex: 1,
                                    }}
                                >
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {t('Modules.Admin.NotificationDetail.lastRunAtLabel')}
                                        </Typography>
                                        <Typography>{formatDate(report.last_run_at)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {t('Modules.Admin.NotificationDetail.nextRunAtLabel')}
                                        </Typography>
                                        <Typography>{formatDate(report.next_run_at)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {t(
                                                'Modules.Admin.NotificationDetail.scheduleStatusLabel',
                                            )}
                                        </Typography>
                                        <Typography>
                                            {report.is_paused
                                                ? t(
                                                      'Modules.Admin.NotificationDetail.pausedStatusLabel',
                                                  )
                                                : t(
                                                      'Modules.Admin.NotificationDetail.activeStatusLabel',
                                                  )}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {t('Modules.Admin.NotificationDetail.intervalLabel')}
                                        </Typography>
                                        <Typography>{report.interval}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {t('Modules.Admin.NotificationDetail.createdAtLabel')}
                                        </Typography>
                                        <Typography>{formatDate(report.created_at)}</Typography>
                                    </Box>
                                </Box>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row', md: 'column' }}
                                    spacing={1}
                                    alignItems={{ xs: 'flex-start', md: 'flex-end' }}
                                    justifyContent="flex-start"
                                >
                                    <Button
                                        variant="outlined"
                                        onClick={handlePauseToggle}
                                        disabled={
                                            updateState?.state === NetworkRequestState.InProgress
                                        }
                                    >
                                        {report.is_paused
                                            ? t(
                                                  'Modules.Admin.NotificationDetail.resumeScheduleLabel',
                                              )
                                            : t(
                                                  'Modules.Admin.NotificationDetail.pauseScheduleLabel',
                                              )}
                                    </Button>
                                    <Button onClick={getPreviewHandler} disabled={isPreviewLoading}>
                                        {isPreviewLoading
                                            ? t(
                                                  'Modules.Admin.NotificationDetail.previewSendingLabel',
                                              )
                                            : t(
                                                  'Modules.Admin.NotificationDetail.sendPreviewLabel',
                                              )}
                                    </Button>
                                </Stack>
                            </Stack>
                            {previewError && (
                                <FlashMessage status="error">{previewError}</FlashMessage>
                            )}
                            {!previewError && previewSuccessMessage && (
                                <FlashMessage status="success">
                                    {previewSuccessMessage}
                                </FlashMessage>
                            )}
                        </Stack>
                    )}
                </CardContent>
            </Card>
            {Number.isFinite(reportId) && report && (
                <Card>
                    <CardHeader title={t('Modules.Admin.NotificationDetail.detailsTitle')} />
                    <CardContent>
                        <Stack spacing={2}>
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={2}
                                alignItems="stretch"
                            >
                                <Box flex={1}>
                                    <TextField
                                        label={t(
                                            'Modules.Admin.NotificationDetail.reportNameLabel',
                                        )}
                                        value={nameDraft}
                                        onChange={event => setNameDraft(event.target.value)}
                                    />
                                </Box>
                                <Box flex={1}>
                                    <TextField
                                        label={t('Modules.Admin.NotificationDetail.intervalLabel')}
                                        value={intervalDraft}
                                        onChange={event => setIntervalDraft(event.target.value)}
                                    />
                                </Box>
                            </Stack>
                            <TextField
                                label={t('Modules.Admin.NotificationDetail.emailSubjectLabel')}
                                value={subjectDraft}
                                onChange={event => setSubjectDraft(event.target.value)}
                            />
                            <TextField
                                label={t('Modules.Admin.NotificationDetail.emailBodyLabel')}
                                value={bodyDraft}
                                onChange={event => setBodyDraft(event.target.value)}
                                multiline
                                minRows={3}
                            />
                            <TextField
                                label={t('Modules.Admin.NotificationDetail.recipientsLabel')}
                                value={emailsDraft}
                                onChange={event => setEmailsDraft(event.target.value)}
                                multiline
                                minRows={4}
                            />
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Button
                                    variant="contained"
                                    onClick={handleNotificationUpdate}
                                    disabled={isUpdateNotificationDisabled}
                                >
                                    {t('Modules.Admin.NotificationDetail.updateNotificationLabel')}
                                </Button>
                                {updateState?.state === NetworkRequestState.Error && (
                                    <Typography color="error" variant="body2">
                                        {String(updateState?.error ?? '')}
                                    </Typography>
                                )}
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
            )}
            {deletedToast && (
                <FlashMessage status="success">
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Typography>
                            {t('Modules.Admin.NotificationDetail.queryDeletedLabel')}
                        </Typography>
                        <Button variant="text" onClick={handleUndoDelete}>
                            {t('Modules.Admin.NotificationDetail.undoDeleteLabel')}
                        </Button>
                    </Stack>
                </FlashMessage>
            )}
            {Number.isFinite(reportId) && report && (
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h4">
                            {t('Modules.Admin.NotificationDetail.queriesTitle')}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={2}>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1.5}
                                alignItems={{ xs: 'stretch', sm: 'center' }}
                                justifyContent="space-between"
                            >
                                <Typography variant="body2" color="text.secondary">
                                    {t('Modules.Admin.NotificationDetail.queryCountLabel', {
                                        saved: visibleQueries.length,
                                        draft: draftQueries.length,
                                    })}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                    <Button variant="outlined" onClick={handleAddQuery}>
                                        {t('Modules.Admin.NotificationDetail.addQueryLabel')}
                                    </Button>
                                    <Button
                                        variant="text"
                                        onClick={toggleAllQueriesExpanded}
                                        disabled={existingQueryKeys.length === 0}
                                    >
                                        {areAllQueriesExpanded
                                            ? t('Modules.Admin.NotificationDetail.collapseAllLabel')
                                            : t('Modules.Admin.NotificationDetail.expandAllLabel')}
                                    </Button>
                                </Stack>
                            </Stack>
                            {visibleQueries.length === 0 && draftQueries.length === 0 ? (
                                <Typography>
                                    {t('Modules.Admin.NotificationDetail.noQueries')}
                                </Typography>
                            ) : (
                                visibleQueries.map(({ query, index }) => {
                                    const queryKey = getQueryKey(query, index);
                                    const isExpanded = Boolean(expandedQueries[queryKey]);
                                    const queryDraft = getExistingQueryDraft(query, index);
                                    const isDeltaDeliveryEnabled =
                                        queryDraft.send_new_rows || queryDraft.send_updated_rows;
                                    const detection = detectedColumns[queryKey];
                                    const selectedDeltaColumns = normalizeDeltaKeyColumns(
                                        queryDraft.delta_key_columns,
                                    );
                                    const canUseDetectedColumns =
                                        !detection?.error &&
                                        (detection?.columns.length ?? 0) > 0 &&
                                        detectedColumnsContainKeys(
                                            detection?.columns ?? [],
                                            selectedDeltaColumns,
                                        );
                                    const detectedColumnOptions = buildColumnOptions(
                                        detection?.columns ?? [],
                                    );
                                    const filenameLabel =
                                        queryDraft.filename ??
                                        query.filename ??
                                        t('Modules.Admin.NotificationDetail.emptyValue');
                                    const deltaCacheState = deltaCacheStatuses[queryKey];
                                    const baselineState = deltaBaselineStates[queryKey];
                                    const deltaCacheHasError = Boolean(
                                        deltaCacheState?.error ||
                                            deltaCacheState?.data?.is_readable === false,
                                    );
                                    const canClearDeltaCache = Boolean(
                                        deltaCacheState?.data?.has_any_cache,
                                    );
                                    const shouldShowDeltaCacheStatus =
                                        isDeltaDeliveryEnabled || canClearDeltaCache;
                                    const isConfirmingClearDeltaCache = Boolean(
                                        confirmingClearDeltaCache[queryKey],
                                    );
                                    let clearDeltaCacheButtonLabel = t(
                                        'Modules.Admin.NotificationDetail.clearDeltaCacheLabel',
                                    );
                                    if (isConfirmingClearDeltaCache) {
                                        clearDeltaCacheButtonLabel = t(
                                            'Modules.Admin.NotificationDetail.clearDeltaCacheConfirmLabel',
                                        );
                                    }
                                    if (deltaCacheState?.isClearing) {
                                        clearDeltaCacheButtonLabel = t(
                                            'Modules.Admin.NotificationDetail.clearDeltaCacheClearingLabel',
                                        );
                                    }
                                    let seedBaselineButtonLabel = t(
                                        'Modules.Admin.NotificationDetail.seedDeltaBaselineLabel',
                                    );
                                    if (baselineState?.isConfirming) {
                                        seedBaselineButtonLabel = t(
                                            'Modules.Admin.NotificationDetail.seedDeltaBaselineConfirmLabel',
                                        );
                                    }
                                    if (baselineState?.isQueueing) {
                                        seedBaselineButtonLabel = t(
                                            'Modules.Admin.NotificationDetail.seedDeltaBaselineQueueingLabel',
                                        );
                                    }
                                    return (
                                        <Collapse
                                            key={queryKey}
                                            in={!hiddenQueries[queryKey]}
                                            timeout={200}
                                        >
                                            <Box
                                                sx={{
                                                    ...queryPanelSx,
                                                    transition: 'opacity 200ms ease',
                                                    opacity: deletingQueries[queryKey] ? 0 : 1,
                                                }}
                                            >
                                                <Box sx={queryHeaderSx(isExpanded)}>
                                                    <Stack
                                                        direction={{ xs: 'column', md: 'row' }}
                                                        spacing={1.5}
                                                        alignItems={{
                                                            xs: 'stretch',
                                                            md: 'center',
                                                        }}
                                                        justifyContent="space-between"
                                                    >
                                                        <Stack spacing={0.75} minWidth={0}>
                                                            <Stack
                                                                direction="row"
                                                                spacing={1}
                                                                alignItems="center"
                                                                sx={{
                                                                    flexWrap: 'wrap',
                                                                }}
                                                            >
                                                                <Typography variant="subtitle2">
                                                                    {t(
                                                                        'Modules.Admin.NotificationDetail.dbQueryLabel',
                                                                        { index: index + 1 },
                                                                    )}
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                    sx={{
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                    }}
                                                                >
                                                                    {filenameLabel}
                                                                </Typography>
                                                            </Stack>
                                                            <Stack
                                                                direction="row"
                                                                spacing={1}
                                                                sx={{ flexWrap: 'wrap' }}
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={querySummaryPillSx}
                                                                >
                                                                    {queryDraft.source ||
                                                                        t(
                                                                            'Modules.Admin.NotificationDetail.emptyValue',
                                                                        )}
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={querySummaryPillSx}
                                                                >
                                                                    {isDeltaDeliveryEnabled
                                                                        ? t(
                                                                              'Modules.Admin.NotificationDetail.deltaEnabledLabel',
                                                                          )
                                                                        : t(
                                                                              'Modules.Admin.NotificationDetail.deltaDisabledLabel',
                                                                          )}
                                                                </Typography>
                                                                {shouldShowDeltaCacheStatus && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            ...querySummaryPillSx,
                                                                            color: deltaCacheHasError
                                                                                ? 'error.main'
                                                                                : 'text.secondary',
                                                                        }}
                                                                    >
                                                                        {getDeltaCacheStatusLabel(
                                                                            deltaCacheState,
                                                                        )}
                                                                    </Typography>
                                                                )}
                                                            </Stack>
                                                        </Stack>
                                                        <Stack
                                                            direction="row"
                                                            spacing={1}
                                                            justifyContent={{
                                                                xs: 'flex-start',
                                                                md: 'flex-end',
                                                            }}
                                                            sx={{ flexWrap: 'wrap' }}
                                                        >
                                                            <Button
                                                                variant="outlined"
                                                                onClick={() =>
                                                                    handleCloneExistingQuery(
                                                                        query,
                                                                        index,
                                                                    )
                                                                }
                                                            >
                                                                {t(
                                                                    'Modules.Admin.NotificationDetail.cloneQueryLabel',
                                                                )}
                                                            </Button>
                                                            <Button
                                                                variant="outlined"
                                                                onClick={() =>
                                                                    handleSaveExistingQuery(
                                                                        query,
                                                                        index,
                                                                    )
                                                                }
                                                                disabled={isExistingQuerySaveDisabled(
                                                                    query,
                                                                    index,
                                                                )}
                                                            >
                                                                {t(
                                                                    'Modules.Admin.NotificationDetail.saveQueryLabel',
                                                                )}
                                                            </Button>
                                                            <Button
                                                                variant="outlined"
                                                                color="error"
                                                                onClick={() =>
                                                                    handleDeleteExistingQuery(
                                                                        query,
                                                                        index,
                                                                    )
                                                                }
                                                                disabled={isDeletingQuery}
                                                            >
                                                                {t(
                                                                    'Modules.Admin.NotificationDetail.deleteQueryLabel',
                                                                )}
                                                            </Button>
                                                            <Button
                                                                variant="text"
                                                                endIcon={
                                                                    isExpanded ? (
                                                                        <ExpandLess />
                                                                    ) : (
                                                                        <ExpandMore />
                                                                    )
                                                                }
                                                                onClick={() =>
                                                                    toggleQueryExpanded(queryKey)
                                                                }
                                                            >
                                                                {isExpanded
                                                                    ? t(
                                                                          'Modules.Admin.NotificationDetail.collapseQueryLabel',
                                                                      )
                                                                    : t(
                                                                          'Modules.Admin.NotificationDetail.expandQueryLabel',
                                                                      )}
                                                            </Button>
                                                        </Stack>
                                                    </Stack>
                                                </Box>
                                                <Collapse in={isExpanded} timeout={200}>
                                                    <Stack gap={2} p={2}>
                                                        <Stack
                                                            direction={{ xs: 'column', md: 'row' }}
                                                            spacing={2}
                                                            alignItems={{
                                                                xs: 'stretch',
                                                                md: 'flex-start',
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    minWidth: { md: 180 },
                                                                    width: { xs: '100%', md: 200 },
                                                                }}
                                                            >
                                                                <SelectField
                                                                    value={queryDraft.source}
                                                                    options={sourceOptions}
                                                                    label={t(
                                                                        'Modules.Admin.NotificationDetail.querySourceLabel',
                                                                    )}
                                                                    onChange={handleSourceChange(
                                                                        queryKey,
                                                                        queryDraft,
                                                                    )}
                                                                    multiple={false}
                                                                />
                                                            </Box>
                                                            <Box flex={1}>
                                                                <TextField
                                                                    label={t(
                                                                        'Modules.Admin.NotificationDetail.queryFilenameLabel',
                                                                    )}
                                                                    value={queryDraft.filename}
                                                                    onChange={handleExistingFilenameChange(
                                                                        queryKey,
                                                                        queryDraft,
                                                                    )}
                                                                />
                                                            </Box>
                                                        </Stack>
                                                        <Box sx={querySectionSx}>
                                                            <Stack spacing={1.25}>
                                                                <Typography variant="subtitle2">
                                                                    {t(
                                                                        'Modules.Admin.NotificationDetail.sqlEditorLabel',
                                                                    )}
                                                                </Typography>
                                                                <TextField
                                                                    value={queryDraft.query}
                                                                    multiline
                                                                    minRows={5}
                                                                    onChange={handleExistingQueryChange(
                                                                        queryKey,
                                                                        queryDraft,
                                                                    )}
                                                                />
                                                            </Stack>
                                                        </Box>
                                                        <Box sx={querySectionSx}>
                                                            <Stack spacing={2}>
                                                                <Typography variant="subtitle2">
                                                                    {t(
                                                                        'Modules.Admin.NotificationDetail.deltaDeliveryTitle',
                                                                    )}
                                                                </Typography>
                                                                <Stack
                                                                    direction={{
                                                                        xs: 'column',
                                                                        md: 'row',
                                                                    }}
                                                                    spacing={2}
                                                                    alignItems={{
                                                                        xs: 'flex-start',
                                                                        md: 'center',
                                                                    }}
                                                                >
                                                                    <Checkbox
                                                                        label={t(
                                                                            'Modules.Admin.NotificationDetail.sendNewRowsLabel',
                                                                        )}
                                                                        isBoolean
                                                                        value={
                                                                            queryDraft.send_new_rows
                                                                        }
                                                                        onChange={handleExistingSendNewRowsChange(
                                                                            queryKey,
                                                                            queryDraft,
                                                                        )}
                                                                    />
                                                                    <Checkbox
                                                                        label={t(
                                                                            'Modules.Admin.NotificationDetail.sendUpdatedRowsLabel',
                                                                        )}
                                                                        isBoolean
                                                                        value={
                                                                            queryDraft.send_updated_rows
                                                                        }
                                                                        onChange={handleExistingSendUpdatedRowsChange(
                                                                            queryKey,
                                                                            queryDraft,
                                                                        )}
                                                                    />
                                                                </Stack>
                                                                {isDeltaDeliveryEnabled && (
                                                                    <>
                                                                        <Stack
                                                                            direction={{
                                                                                xs: 'column',
                                                                                md: 'row',
                                                                            }}
                                                                            spacing={1}
                                                                            alignItems={{
                                                                                xs: 'stretch',
                                                                                md: 'flex-start',
                                                                            }}
                                                                        >
                                                                            <Box flex={1}>
                                                                                {canUseDetectedColumns ? (
                                                                                    <Stack
                                                                                        spacing={
                                                                                            0.5
                                                                                        }
                                                                                    >
                                                                                        <SelectField
                                                                                            label={t(
                                                                                                'Modules.Admin.NotificationDetail.deltaKeyColumnsLabel',
                                                                                            )}
                                                                                            value={
                                                                                                selectedDeltaColumns
                                                                                            }
                                                                                            options={
                                                                                                detectedColumnOptions
                                                                                            }
                                                                                            onChange={handleExistingDeltaKeyColumnsChange(
                                                                                                queryKey,
                                                                                                queryDraft,
                                                                                            )}
                                                                                            multiple
                                                                                        />
                                                                                        {selectedDeltaColumns.length ===
                                                                                            0 && (
                                                                                            <Typography
                                                                                                variant="caption"
                                                                                                color="text.secondary"
                                                                                            >
                                                                                                {t(
                                                                                                    'Modules.Admin.NotificationDetail.deltaKeyColumnsPlaceholder',
                                                                                                )}
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Stack>
                                                                                ) : (
                                                                                    <TextField
                                                                                        label={t(
                                                                                            'Modules.Admin.NotificationDetail.deltaKeyColumnsLabel',
                                                                                        )}
                                                                                        value={deltaKeyColumnsToText(
                                                                                            queryDraft.delta_key_columns,
                                                                                        )}
                                                                                        placeholder={t(
                                                                                            'Modules.Admin.NotificationDetail.deltaKeyColumnsPlaceholder',
                                                                                        )}
                                                                                        onChange={handleExistingDeltaKeyColumnsChange(
                                                                                            queryKey,
                                                                                            queryDraft,
                                                                                        )}
                                                                                        helperText={
                                                                                            selectedDeltaColumns.length ===
                                                                                            0
                                                                                                ? t(
                                                                                                      'Modules.Admin.NotificationDetail.deltaKeyColumnsPlaceholder',
                                                                                                  )
                                                                                                : t(
                                                                                                      'Modules.Admin.NotificationDetail.deltaKeyColumnsManualHelper',
                                                                                                  )
                                                                                        }
                                                                                        multiline
                                                                                        minRows={2}
                                                                                    />
                                                                                )}
                                                                            </Box>
                                                                            <Button
                                                                                variant="outlined"
                                                                                onClick={handleDetectColumns(
                                                                                    queryKey,
                                                                                    queryDraft,
                                                                                )}
                                                                                disabled={
                                                                                    detection?.isLoading ||
                                                                                    !queryDraft.source.trim() ||
                                                                                    !queryDraft.query.trim()
                                                                                }
                                                                            >
                                                                                {detection?.isLoading
                                                                                    ? t(
                                                                                          'Modules.Admin.NotificationDetail.detectingColumnsLabel',
                                                                                      )
                                                                                    : t(
                                                                                          'Modules.Admin.NotificationDetail.detectColumnsLabel',
                                                                                      )}
                                                                            </Button>
                                                                        </Stack>
                                                                        {detection?.error && (
                                                                            <FlashMessage status="error">
                                                                                {detection.error}
                                                                            </FlashMessage>
                                                                        )}
                                                                    </>
                                                                )}
                                                                {shouldShowDeltaCacheStatus && (
                                                                    <Stack
                                                                        direction={{
                                                                            xs: 'column',
                                                                            md: 'row',
                                                                        }}
                                                                        spacing={1}
                                                                        alignItems={{
                                                                            xs: 'flex-start',
                                                                            md: 'center',
                                                                        }}
                                                                        justifyContent="space-between"
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            color={
                                                                                deltaCacheHasError
                                                                                    ? 'error'
                                                                                    : 'text.secondary'
                                                                            }
                                                                        >
                                                                            {t(
                                                                                'Modules.Admin.NotificationDetail.deltaCacheLabel',
                                                                            )}
                                                                            :{' '}
                                                                            {getDeltaCacheStatusLabel(
                                                                                deltaCacheState,
                                                                            )}
                                                                        </Typography>
                                                                        <Stack
                                                                            direction="row"
                                                                            spacing={1}
                                                                            sx={{
                                                                                flexWrap: 'wrap',
                                                                            }}
                                                                        >
                                                                            <Button
                                                                                variant="outlined"
                                                                                onClick={handleSeedDeltaBaseline(
                                                                                    query,
                                                                                    index,
                                                                                )}
                                                                                disabled={
                                                                                    baselineState?.isQueueing
                                                                                }
                                                                            >
                                                                                {
                                                                                    seedBaselineButtonLabel
                                                                                }
                                                                            </Button>
                                                                            <Button
                                                                                variant="outlined"
                                                                                color="error"
                                                                                onClick={handleClearDeltaCache(
                                                                                    query,
                                                                                    index,
                                                                                )}
                                                                                disabled={
                                                                                    !canClearDeltaCache ||
                                                                                    deltaCacheState?.isLoading ||
                                                                                    deltaCacheState?.isClearing
                                                                                }
                                                                            >
                                                                                {
                                                                                    clearDeltaCacheButtonLabel
                                                                                }
                                                                            </Button>
                                                                        </Stack>
                                                                    </Stack>
                                                                )}
                                                                {baselineState?.message && (
                                                                    <FlashMessage status="success">
                                                                        {baselineState.message}
                                                                    </FlashMessage>
                                                                )}
                                                                {baselineState?.error && (
                                                                    <FlashMessage status="error">
                                                                        {baselineState.error}
                                                                    </FlashMessage>
                                                                )}
                                                            </Stack>
                                                        </Box>
                                                    </Stack>
                                                    {(updateQueryError || deleteQueryError) && (
                                                        <Typography color="error" variant="body2">
                                                            {String(
                                                                updateQueryError ??
                                                                    deleteQueryError,
                                                            )}
                                                        </Typography>
                                                    )}
                                                </Collapse>
                                            </Box>
                                        </Collapse>
                                    );
                                })
                            )}
                            {draftQueries.map((query, index) => {
                                const queryKey = query.id;
                                const isExpanded = Boolean(expandedQueries[queryKey]);
                                const isDeltaDeliveryEnabled =
                                    query.send_new_rows || query.send_updated_rows;
                                const detection = detectedColumns[queryKey];
                                const selectedDeltaColumns = normalizeDeltaKeyColumns(
                                    query.delta_key_columns,
                                );
                                const canUseDetectedColumns =
                                    !detection?.error &&
                                    (detection?.columns.length ?? 0) > 0 &&
                                    detectedColumnsContainKeys(
                                        detection?.columns ?? [],
                                        selectedDeltaColumns,
                                    );
                                const detectedColumnOptions = buildColumnOptions(
                                    detection?.columns ?? [],
                                );
                                const filenameLabel =
                                    query.filename.trim() !== ''
                                        ? query.filename
                                        : t('Modules.Admin.NotificationDetail.emptyValue');
                                return (
                                    <Box key={query.id} sx={queryPanelSx}>
                                        <Box sx={queryHeaderSx(isExpanded)}>
                                            <Stack
                                                direction={{ xs: 'column', md: 'row' }}
                                                spacing={1.5}
                                                alignItems={{ xs: 'stretch', md: 'center' }}
                                                justifyContent="space-between"
                                            >
                                                <Stack spacing={0.75} minWidth={0}>
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        alignItems="center"
                                                        sx={{ flexWrap: 'wrap' }}
                                                    >
                                                        <Typography variant="subtitle2">
                                                            {t(
                                                                'Modules.Admin.NotificationDetail.dbQueryLabel',
                                                                {
                                                                    index:
                                                                        visibleQueries.length +
                                                                        index +
                                                                        1,
                                                                },
                                                            )}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                            }}
                                                        >
                                                            {filenameLabel}
                                                        </Typography>
                                                    </Stack>
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        sx={{ flexWrap: 'wrap' }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={querySummaryPillSx}
                                                        >
                                                            {query.source ||
                                                                t(
                                                                    'Modules.Admin.NotificationDetail.emptyValue',
                                                                )}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={querySummaryPillSx}
                                                        >
                                                            {t(
                                                                'Modules.Admin.NotificationDetail.queryUnsavedLabel',
                                                            )}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={querySummaryPillSx}
                                                        >
                                                            {isDeltaDeliveryEnabled
                                                                ? t(
                                                                      'Modules.Admin.NotificationDetail.deltaEnabledLabel',
                                                                  )
                                                                : t(
                                                                      'Modules.Admin.NotificationDetail.deltaDisabledLabel',
                                                                  )}
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    justifyContent={{
                                                        xs: 'flex-start',
                                                        md: 'flex-end',
                                                    }}
                                                    sx={{ flexWrap: 'wrap' }}
                                                >
                                                    <Button
                                                        variant="outlined"
                                                        onClick={() => handleCloneDraftQuery(query)}
                                                    >
                                                        {t(
                                                            'Modules.Admin.NotificationDetail.cloneQueryLabel',
                                                        )}
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() =>
                                                            handleDeleteDraftQuery(query.id)
                                                        }
                                                    >
                                                        {t(
                                                            'Modules.Admin.NotificationDetail.deleteQueryLabel',
                                                        )}
                                                    </Button>
                                                    <Button
                                                        variant="text"
                                                        endIcon={
                                                            isExpanded ? (
                                                                <ExpandLess />
                                                            ) : (
                                                                <ExpandMore />
                                                            )
                                                        }
                                                        onClick={() =>
                                                            toggleQueryExpanded(queryKey)
                                                        }
                                                    >
                                                        {isExpanded
                                                            ? t(
                                                                  'Modules.Admin.NotificationDetail.collapseQueryLabel',
                                                              )
                                                            : t(
                                                                  'Modules.Admin.NotificationDetail.expandQueryLabel',
                                                              )}
                                                    </Button>
                                                </Stack>
                                            </Stack>
                                        </Box>
                                        <Collapse in={isExpanded} timeout={200}>
                                            <Stack gap={2} p={2}>
                                                <Stack
                                                    direction={{ xs: 'column', md: 'row' }}
                                                    spacing={2}
                                                    alignItems={{
                                                        xs: 'stretch',
                                                        md: 'flex-start',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            minWidth: { md: 180 },
                                                            width: { xs: '100%', md: 200 },
                                                        }}
                                                    >
                                                        <SelectField
                                                            value={query.source}
                                                            options={sourceOptions}
                                                            label={t(
                                                                'Modules.Admin.NotificationDetail.querySourceLabel',
                                                            )}
                                                            onChange={handleDraftSourceChange(
                                                                query.id,
                                                            )}
                                                            multiple={false}
                                                        />
                                                    </Box>
                                                    <Box flex={1}>
                                                        <TextField
                                                            label={t(
                                                                'Modules.Admin.NotificationDetail.queryFilenameLabel',
                                                            )}
                                                            value={query.filename}
                                                            onChange={handleDraftFilenameChange(
                                                                query.id,
                                                            )}
                                                        />
                                                    </Box>
                                                </Stack>
                                                <Box sx={querySectionSx}>
                                                    <Stack spacing={1.25}>
                                                        <Typography variant="subtitle2">
                                                            {t(
                                                                'Modules.Admin.NotificationDetail.sqlEditorLabel',
                                                            )}
                                                        </Typography>
                                                        <TextField
                                                            value={query.query}
                                                            onChange={handleDraftQueryChange(
                                                                query.id,
                                                            )}
                                                            multiline
                                                            minRows={5}
                                                        />
                                                    </Stack>
                                                </Box>
                                                <Box sx={querySectionSx}>
                                                    <Stack spacing={2}>
                                                        <Typography variant="subtitle2">
                                                            {t(
                                                                'Modules.Admin.NotificationDetail.deltaDeliveryTitle',
                                                            )}
                                                        </Typography>
                                                        <Stack
                                                            direction={{ xs: 'column', md: 'row' }}
                                                            spacing={2}
                                                            alignItems={{
                                                                xs: 'flex-start',
                                                                md: 'center',
                                                            }}
                                                        >
                                                            <Checkbox
                                                                label={t(
                                                                    'Modules.Admin.NotificationDetail.sendNewRowsLabel',
                                                                )}
                                                                isBoolean
                                                                value={query.send_new_rows}
                                                                onChange={handleDraftSendNewRowsChange(
                                                                    query.id,
                                                                )}
                                                            />
                                                            <Checkbox
                                                                label={t(
                                                                    'Modules.Admin.NotificationDetail.sendUpdatedRowsLabel',
                                                                )}
                                                                isBoolean
                                                                value={query.send_updated_rows}
                                                                onChange={handleDraftSendUpdatedRowsChange(
                                                                    query.id,
                                                                )}
                                                            />
                                                        </Stack>
                                                        {isDeltaDeliveryEnabled && (
                                                            <>
                                                                <Stack
                                                                    direction={{
                                                                        xs: 'column',
                                                                        md: 'row',
                                                                    }}
                                                                    spacing={1}
                                                                    alignItems={{
                                                                        xs: 'stretch',
                                                                        md: 'flex-start',
                                                                    }}
                                                                >
                                                                    <Box flex={1}>
                                                                        {canUseDetectedColumns ? (
                                                                            <Stack spacing={0.5}>
                                                                                <SelectField
                                                                                    label={t(
                                                                                        'Modules.Admin.NotificationDetail.deltaKeyColumnsLabel',
                                                                                    )}
                                                                                    value={
                                                                                        selectedDeltaColumns
                                                                                    }
                                                                                    options={
                                                                                        detectedColumnOptions
                                                                                    }
                                                                                    onChange={handleDraftDeltaKeyColumnsChange(
                                                                                        query.id,
                                                                                    )}
                                                                                    multiple
                                                                                />
                                                                                {selectedDeltaColumns.length ===
                                                                                    0 && (
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        color="text.secondary"
                                                                                    >
                                                                                        {t(
                                                                                            'Modules.Admin.NotificationDetail.deltaKeyColumnsPlaceholder',
                                                                                        )}
                                                                                    </Typography>
                                                                                )}
                                                                            </Stack>
                                                                        ) : (
                                                                            <TextField
                                                                                label={t(
                                                                                    'Modules.Admin.NotificationDetail.deltaKeyColumnsLabel',
                                                                                )}
                                                                                value={deltaKeyColumnsToText(
                                                                                    query.delta_key_columns,
                                                                                )}
                                                                                placeholder={t(
                                                                                    'Modules.Admin.NotificationDetail.deltaKeyColumnsPlaceholder',
                                                                                )}
                                                                                onChange={handleDraftDeltaKeyColumnsChange(
                                                                                    query.id,
                                                                                )}
                                                                                helperText={
                                                                                    selectedDeltaColumns.length ===
                                                                                    0
                                                                                        ? t(
                                                                                              'Modules.Admin.NotificationDetail.deltaKeyColumnsPlaceholder',
                                                                                          )
                                                                                        : t(
                                                                                              'Modules.Admin.NotificationDetail.deltaKeyColumnsManualHelper',
                                                                                          )
                                                                                }
                                                                                multiline
                                                                                minRows={2}
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                    <Button
                                                                        variant="outlined"
                                                                        onClick={handleDetectColumns(
                                                                            query.id,
                                                                            query,
                                                                        )}
                                                                        disabled={
                                                                            detection?.isLoading ||
                                                                            !query.source.trim() ||
                                                                            !query.query.trim()
                                                                        }
                                                                    >
                                                                        {detection?.isLoading
                                                                            ? t(
                                                                                  'Modules.Admin.NotificationDetail.detectingColumnsLabel',
                                                                              )
                                                                            : t(
                                                                                  'Modules.Admin.NotificationDetail.detectColumnsLabel',
                                                                              )}
                                                                    </Button>
                                                                </Stack>
                                                                {detection?.error && (
                                                                    <FlashMessage status="error">
                                                                        {detection.error}
                                                                    </FlashMessage>
                                                                )}
                                                            </>
                                                        )}
                                                    </Stack>
                                                </Box>
                                            </Stack>
                                        </Collapse>
                                    </Box>
                                );
                            })}
                            {(draftQueries.length > 0 || saveError) && (
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Button
                                        variant="contained"
                                        onClick={handleSaveQueries}
                                        disabled={!canSaveQueries || isSavingQueries}
                                    >
                                        {t('Modules.Admin.NotificationDetail.saveQueriesLabel')}
                                    </Button>
                                    {saveError && (
                                        <Typography color="error" variant="body2">
                                            {String(saveError)}
                                        </Typography>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            )}
            {Number.isFinite(reportId) && report && (
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h4">
                            {t('Modules.Admin.NotificationDetail.runHistoryTitle')}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Button
                                    variant="outlined"
                                    onClick={() => refreshReportRuns().catch(() => undefined)}
                                    disabled={reportRuns.isLoading}
                                >
                                    {reportRuns.isLoading
                                        ? t(
                                              'Modules.Admin.NotificationDetail.reportRunsLoadingLabel',
                                          )
                                        : t(
                                              'Modules.Admin.NotificationDetail.refreshRunHistoryLabel',
                                          )}
                                </Button>
                            </Stack>
                            {reportRuns.error && (
                                <FlashMessage status="error">{reportRuns.error}</FlashMessage>
                            )}
                            {!reportRuns.isLoading && reportRuns.data.length === 0 && (
                                <Typography color="text.secondary">
                                    {t('Modules.Admin.NotificationDetail.noReportRunsLabel')}
                                </Typography>
                            )}
                            {reportRuns.data.map(run => {
                                const runKey = String(run.id);
                                const isRunExpanded = Boolean(expandedRunKeys[runKey]);
                                const queryRuns = queryRunsOf(run);

                                return (
                                    <Box
                                        key={runKey}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            p: 2,
                                        }}
                                    >
                                        <Stack spacing={1.5}>
                                            <Stack
                                                direction={{ xs: 'column', md: 'row' }}
                                                spacing={1.5}
                                                justifyContent="space-between"
                                                alignItems={{ xs: 'flex-start', md: 'center' }}
                                            >
                                                <Stack spacing={0.5}>
                                                    <Typography variant="subtitle2">
                                                        {t(
                                                            'Modules.Admin.NotificationDetail.reportRunSummaryLabel',
                                                            {
                                                                type: run.run_type,
                                                                status: run.status,
                                                            },
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {formatDate(run.started_at)} -{' '}
                                                        {formatDate(run.completed_at)}
                                                    </Typography>
                                                </Stack>
                                                <Button
                                                    variant="text"
                                                    endIcon={
                                                        isRunExpanded ? (
                                                            <ExpandLess />
                                                        ) : (
                                                            <ExpandMore />
                                                        )
                                                    }
                                                    onClick={() => toggleRunExpanded(runKey)}
                                                >
                                                    {isRunExpanded
                                                        ? t(
                                                              'Modules.Admin.NotificationDetail.collapseRunLabel',
                                                          )
                                                        : t(
                                                              'Modules.Admin.NotificationDetail.expandRunLabel',
                                                          )}
                                                </Button>
                                            </Stack>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: {
                                                        xs: '1fr',
                                                        sm: 'repeat(2, minmax(0, 1fr))',
                                                        lg: 'repeat(4, minmax(0, 1fr))',
                                                    },
                                                    gap: 1.5,
                                                }}
                                            >
                                                <Typography variant="body2">
                                                    {t(
                                                        'Modules.Admin.NotificationDetail.reportRunRowsLabel',
                                                        {
                                                            sent: run.total_sent_row_count,
                                                            current: run.total_current_row_count,
                                                        },
                                                    )}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {t(
                                                        'Modules.Admin.NotificationDetail.reportRunDeliveryLabel',
                                                        {
                                                            mode:
                                                                run.delivery_mode ??
                                                                t(
                                                                    'Modules.Admin.NotificationDetail.emptyValue',
                                                                ),
                                                        },
                                                    )}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {t(
                                                        'Modules.Admin.NotificationDetail.reportRunFileLabel',
                                                        {
                                                            file:
                                                                run.output_filename ??
                                                                t(
                                                                    'Modules.Admin.NotificationDetail.emptyValue',
                                                                ),
                                                            size: formatBytes(
                                                                run.output_file_size_bytes,
                                                            ),
                                                        },
                                                    )}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {t(
                                                        'Modules.Admin.NotificationDetail.reportRunRecipientsLabel',
                                                        {
                                                            count:
                                                                run.recipient_emails?.length ?? 0,
                                                        },
                                                    )}
                                                </Typography>
                                            </Box>
                                            {(run.notes || run.error_message) && (
                                                <Typography
                                                    variant="body2"
                                                    color={
                                                        run.error_message
                                                            ? 'error'
                                                            : 'text.secondary'
                                                    }
                                                >
                                                    {run.error_message ?? run.notes}
                                                </Typography>
                                            )}
                                            <Collapse in={isRunExpanded} timeout={200}>
                                                <Stack spacing={1} pt={1}>
                                                    {queryRuns.length === 0 ? (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            {t(
                                                                'Modules.Admin.NotificationDetail.noReportQueryRunsLabel',
                                                            )}
                                                        </Typography>
                                                    ) : (
                                                        queryRuns.map(queryRun => (
                                                            <Box
                                                                key={queryRun.id}
                                                                sx={{
                                                                    borderTop: '1px solid',
                                                                    borderColor: 'divider',
                                                                    pt: 1,
                                                                }}
                                                            >
                                                                <Stack spacing={0.5}>
                                                                    <Typography variant="body2">
                                                                        {t(
                                                                            'Modules.Admin.NotificationDetail.queryRunSummaryLabel',
                                                                            {
                                                                                filename:
                                                                                    queryRun.filename ??
                                                                                    t(
                                                                                        'Modules.Admin.NotificationDetail.emptyValue',
                                                                                    ),
                                                                                status: queryRun.status,
                                                                            },
                                                                        )}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                    >
                                                                        {t(
                                                                            'Modules.Admin.NotificationDetail.queryRunDetailLabel',
                                                                            {
                                                                                sent:
                                                                                    queryRun.sent_row_count ??
                                                                                    0,
                                                                                current:
                                                                                    queryRun.current_row_count ??
                                                                                    0,
                                                                                size: formatBytes(
                                                                                    queryRun.output_file_size_bytes,
                                                                                ),
                                                                                cache: queryRun.cache_written
                                                                                    ? t(
                                                                                          'Modules.Admin.NotificationDetail.yesLabel',
                                                                                      )
                                                                                    : t(
                                                                                          'Modules.Admin.NotificationDetail.noLabel',
                                                                                      ),
                                                                            },
                                                                        )}
                                                                    </Typography>
                                                                    {queryRun.error_message && (
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="error"
                                                                        >
                                                                            {queryRun.error_message}
                                                                        </Typography>
                                                                    )}
                                                                </Stack>
                                                            </Box>
                                                        ))
                                                    )}
                                                </Stack>
                                            </Collapse>
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            )}
        </Stack>
    );
};

const setup: PageEntityConfig<NotificationReport> = {
    id: 'NotificationDetailPage',
    component: NotificationDetailPage,
    systemSearch: {
        title: 'Modules.Admin.NotificationDetail.metaTitle',
        description: 'Modules.Admin.NotificationDetail.description',
        icon: notificationsIcon,
        type: 'entity',
        aliases: undefined,
        route: PostAuthRoutes.Admin.NotificationDetail,
        saveAsRecentPage: true,
        defaultIncludes: ['queries'],
        entityName: 'NotificationReport',
        breadcrumbs: [
            { label: 'Modules.Admin.Home.metaTitle', link: PostAuthRoutes.Admin.AdminHome },
            {
                label: 'Modules.Admin.NotificationsList.metaTitle',
                link: PostAuthRoutes.Admin.NotificationsList,
            },
            { label: 'Modules.Admin.NotificationDetail.metaTitle' },
        ],
        nameGen: (entity, id) => entity?.name ?? String(id),
        permissions: [PermissionName.Admin],
    },
};

export default setup;
